import React, { memo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';
import { SpinnerBlock } from 'Components/SpinnerBlock';
import { PurpleButton } from 'Components/Button';
import { Modal } from 'Components/Modal';
import { EquipmentTypeModal } from 'Components/SerializedEquipment';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

import {
  listCompanyEquipmentTypes,
  createCompanyEquipmentType,
  updateCompanyEquipmentType,
  deleteCompanyEquipmentType,
} from '../actions';
import { EquipmentType } from '../types';
import classes from './serializedEquipmentTypes.module.css';

interface Props {
  onChanged?: () => void;
}

// Manage the company's custom equipment types (equipment_inventories). Standard enum types are
// fixed and not listed here. On any change, refreshes the list and notifies the parent so the
// register dropdown's catalog reloads.
function SerializedEquipmentTypesContainer({ onChanged }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<'add' | 'edit'>('add');
  const [editTarget, setEditTarget] = useState<EquipmentType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EquipmentType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const notifyChanged = useCallback(() => {
    if (onChanged) onChanged();
  }, [onChanged]);

  const loadTypes = useCallback(async () => {
    if (!companyId) {
      setFetching(false);
      return;
    }
    setFetching(true);
    setFailed(false);
    const response: any = await dispatch(listCompanyEquipmentTypes(companyId));
    if (!response) {
      setFailed(true);
      setFetching(false);
      return;
    }
    setTypes(response?.data ?? []);
    setFetching(false);
  }, [companyId, dispatch]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const openAdd = useCallback(() => {
    setMode('add');
    setEditTarget(null);
    setSubmitError(null);
    setModalOpen(true);
  }, []);
  const openEdit = useCallback((type: EquipmentType) => {
    setMode('edit');
    setEditTarget(type);
    setSubmitError(null);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const onSubmit = useCallback(
    async (name: string) => {
      if (!companyId) return;
      setSubmitting(true);
      setSubmitError(null);
      const response: any = editTarget
        ? await dispatch(updateCompanyEquipmentType(editTarget.id, name))
        : await dispatch(createCompanyEquipmentType(companyId, name));
      setSubmitting(false);
      if (response?.data) {
        setModalOpen(false);
        loadTypes();
        notifyChanged();
      } else {
        setSubmitError('Couldn’t save the type — a type with that name may already exist.');
      }
    },
    [companyId, editTarget, dispatch, loadTypes, notifyChanged]
  );

  const openDelete = useCallback((type: EquipmentType) => {
    setDeleteError(null);
    setDeleteTarget(type);
  }, []);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const onConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    // DELETE -> 204; success is a string (delete quirk).
    const response: any = await dispatch(deleteCompanyEquipmentType(deleteTarget.id));
    setDeleting(false);
    if (typeof response === 'string') {
      setDeleteTarget(null);
      loadTypes();
      notifyChanged();
    } else {
      setDeleteError('Couldn’t delete this type — it may be in use by existing equipment.');
    }
  }, [deleteTarget, dispatch, loadTypes, notifyChanged]);

  return (
    <div data-testid="serialized-equipment-types">
      <div className={classes.header}>
        <PurpleButton type="button" className={classes.addButton} onClick={openAdd}>
          Add type +
        </PurpleButton>
      </div>

      {fetching && <SpinnerBlock fetching />}
      {!fetching && failed && (
        <div className="d-flex flex-column align-items-center p-3" role="alert" data-testid="types-error">
          <p className="text-muted mb-2">Couldn’t load equipment types.</p>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadTypes}>
            Retry
          </button>
        </div>
      )}
      {!fetching && !failed && types.length === 0 && (
        <p className={classes.empty} data-testid="types-empty">
          No custom equipment types yet.
        </p>
      )}
      {!fetching && !failed && types.length > 0 && (
        <ul className={classes.list} data-testid="types-list">
          {types.map((type) => (
            <li key={type.id} className={classes.row} data-testid="type-row">
              <span className={classes.name}>{type.name}</span>
              <span className={classes.actions}>
                <button type="button" className="btn btn-link btn-sm" onClick={() => openEdit(type)}>
                  Edit
                </button>
                <button type="button" className="btn btn-link btn-sm text-danger" onClick={() => openDelete(type)}>
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <EquipmentTypeModal
        isOpen={modalOpen}
        mode={mode}
        initialName={editTarget?.name ?? ''}
        submitting={submitting}
        submitError={submitError}
        onSubmit={onSubmit}
        onClose={closeModal}
      />

      <Modal
        id="delete-equipment-type-modal"
        title="Delete Equipment Type"
        isOpen={!!deleteTarget}
        modalHeader
        dataBsBackdrop="static"
        dataBsKeyboard="false"
        modalCloseClick={closeDelete}
      >
        <div className="d-flex flex-column w-100" data-testid="delete-type-modal">
          <p>
            Delete
            {' '}
            <strong>{deleteTarget?.name}</strong>
            ? Equipment already registered as this type keeps its name.
          </p>
          {deleteError && (
            <p className={classes.error} role="alert" data-testid="delete-type-error">
              {deleteError}
            </p>
          )}
          <div className={`modal-footer ${classes.modalFooter}`}>
            <PurpleButton type="button" onClick={closeDelete}>
              Cancel
            </PurpleButton>
            <PurpleButton type="button" onClick={onConfirmDelete} disabled={deleting} useDeleteColor>
              Delete
            </PurpleButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

SerializedEquipmentTypesContainer.defaultProps = {
  onChanged: undefined,
};

const SerializedEquipmentTypesMemo = memo(SerializedEquipmentTypesContainer, areEqual);

export { SerializedEquipmentTypesMemo as SerializedEquipmentTypes };
